import { Injectable } from '@angular/core';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { DatasetFormatType, DatasetTypeOptions } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DatasetRegistryService } from '../dataset/dataset-registry.service';
import { ISelectGroupedOption, ISelectOption } from 'app/interfaces/ISelectOption';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { LinkedService, Metamodel } from 'shell-models-lib';
import { IDataflowFormatTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { calcGroupedDataflowTypeOptions } from 'app/extensibility/utils/dynamic-control.util';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { GuardFeatures } from 'app/services/exposure-control/ec.typings';

@Injectable({
    providedIn: 'root'
})
export class DataflowFormatRegistryService {
    private _dataflowInlineConfigurations: IDataflowFormatTypeMetadata[] = [];
    private readonly _dataflowInlineConfigurationMap: Map<string, IDataflowFormatTypeMetadata> = new Map<
        string,
        IDataflowFormatTypeMetadata
    >();

    public get inlineSourceTypeOptions(): Array<ISelectOption<string>> {
        return this._dataflowInlineConfigurations
            .filter(
                (value) =>
                    !this._inlineSourceHideByFeatureFlag(value.type) &&
                    value.supportSource &&
                    !value.notShowInTypeSelection
            )
            .map(({ displayName: label, type: value, icon: { className: labelIcon } }) => ({ label, value, labelIcon }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    public get inlineSinkTypeOptions(): Array<ISelectOption<string>> {
        return this._dataflowInlineConfigurations
            .filter(
                (value) =>
                    !this._inlineSinkHideByFeatureFlag(value.type) && value.supportSink && !value.notShowInTypeSelection
            )
            .map(({ displayName: label, type: value, icon: { className: labelIcon } }) => ({ label, value, labelIcon }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * MDF: sub-format options for "Microsoft Fabric Lakehouse (Files)". Driven by the `mdfFabric`
     * metadata on each format so new formats can opt in declaratively (no hardcoded list).
     */
    public get lakehouseFileFormatOptions(): Array<ISelectOption<DatasetFormatType>> {
        return this._dataflowInlineConfigurations
            .filter((config) => config.mdfFabric?.isLakehouseFileFormat)
            .sort((a, b) => (a.mdfFabric?.order ?? 0) - (b.mdfFabric?.order ?? 0))
            .map((config) => ({
                label: config.displayName,
                value: config.type as DatasetFormatType,
                labelIcon: config.icon?.className
            }));
    }

    public get inlineSourceGroupOptions(): Array<ISelectGroupedOption<string>> {
        return calcGroupedDataflowTypeOptions(
            this._dataflowInlineConfigurations.filter(
                (value) =>
                    !this._inlineSourceHideByFeatureFlag(value.type) &&
                    value.supportSource &&
                    !value.notShowInTypeSelection &&
                    !value.isCDCOnly
            )
        );
    }

    public get inlineCDCSourceGroupOptions(): Array<ISelectGroupedOption<string>> {
        return calcGroupedDataflowTypeOptions(
            this._dataflowInlineConfigurations.filter(
                (value) =>
                    !this._inlineSourceHideByFeatureFlag(value.type) &&
                    value.supportSource &&
                    !value.notShowInTypeSelection
            )
        );
    }

    public get inlineSinkGroupOptions(): Array<ISelectGroupedOption<string>> {
        return calcGroupedDataflowTypeOptions(
            this._dataflowInlineConfigurations.filter(
                (value) =>
                    !this._inlineSinkHideByFeatureFlag(value.type) &&
                    value.supportSink &&
                    !value.notShowInTypeSelection &&
                    !value.isCDCOnly
            )
        );
    }

    public get inlineCDCSinkGroupOptions(): Array<ISelectGroupedOption<string>> {
        return calcGroupedDataflowTypeOptions(
            this._dataflowInlineConfigurations.filter(
                (value) =>
                    !this._inlineSinkHideByFeatureFlag(value.type) && value.supportSink && !value.notShowInTypeSelection
            )
        );
    }

    constructor(
        private readonly _datasetRegistryService: DatasetRegistryService,
        private readonly _exposureControlService: ExposureControlService
    ) {}

    public registerInlineConfig(...config: IDataflowFormatTypeMetadata[]): void {
        this._dataflowInlineConfigurations.push(...config);
        config.forEach((metadata) => this._dataflowInlineConfigurationMap.set(metadata.type, metadata));
    }

    public has(type: string): boolean {
        return this._dataflowInlineConfigurationMap.get(type) != null;
    }

    public clear(): void {
        this._dataflowInlineConfigurations = [];
        this._dataflowInlineConfigurationMap.clear();
    }

    public getDataflowFormatConfiguration(formatType: string): IDataflowFormatTypeMetadata {
        return this._dataflowInlineConfigurationMap.get(formatType);
    }

    public getControls(formatType: string, isSink: boolean = false, datasetType?: string): ControlMetadata[] {
        if (formatType === DatasetTypeOptions.Dataset) {
            const datasetMetadata = this._datasetRegistryService.getDatasetConfiguration(datasetType);
            if (datasetMetadata != null) {
                return isSink ? datasetMetadata.dataflowSinkControls : datasetMetadata.dataflowSourceControls;
            }
            return undefined;
        }
        const metadata = this.getDataflowFormatConfiguration(formatType);
        if (metadata != null) {
            return isSink ? metadata.sinkControls : metadata.sourceControls;
        }
        return undefined;
    }

    public getSupportedSourceLsTypesByFormat(format: DatasetFormatType | string): string[] {
        return (
            this._dataflowInlineConfigurationMap
                .get(format)
                ?.linkedServiceSourceTypes.filter(
                    (linkedService) => !this._linkedServiceHideByFeatureFlag(linkedService, false)
                ) ?? []
        );
    }

    public getSupportedSinkLsTypesByFormat(format: DatasetFormatType | string): string[] {
        return (
            this._dataflowInlineConfigurationMap
                .get(format)
                ?.linkedServiceSinkTypes.filter(
                    (linkedService) => !this._linkedServiceHideByFeatureFlag(linkedService, true)
                ) ?? []
        );
    }

    public getInlineSettingsModelName(format: DatasetFormatType | string, linkedService?: LinkedService): string {
        const settings = this._dataflowInlineConfigurationMap.get(format)?.settings;
        if (linkedService === undefined) {
            return settings;
        } else {
            const linkedServiceName = Metamodel.modelOf(linkedService).name;
            const metadata = this._dataflowInlineConfigurations.filter(
                (metadata) =>
                    metadata.linkedServiceSourceTypes.includes(linkedServiceName) ||
                    metadata.linkedServiceSinkTypes.includes(linkedServiceName)
            );
            return metadata.length === 1 ? metadata[0].settings : settings;
        }
    }

    public getUseInlineProjection(format: DatasetFormatType | string): boolean {
        return format === DatasetFormatType.SqlDBDeprecated
            ? true
            : Boolean(this.getDataflowFormatConfiguration(format)?.useInlineProjection);
    }

    public getEnableImportSchemaInSink(format: DatasetFormatType | string): boolean {
        return format === DatasetFormatType.SqlDBDeprecated
            ? true
            : Boolean(this.getDataflowFormatConfiguration(format)?.enableImportSchemaInSink);
    }

    public getEnableAlterRowInSink(format: DatasetFormatType | string): boolean {
        return format === DatasetFormatType.SqlDBDeprecated
            ? true
            : Boolean(this.getDataflowFormatConfiguration(format)?.enableAlterRow);
    }

    private _inlineSourceHideByFeatureFlag(format: string): boolean {
        switch (format) {
            case DatasetFormatType.AdobeCampaign:
            case DatasetFormatType.AdobeIOEvents:
                return !GlobalVariables.enableAdobeIntegration;
            case DatasetFormatType.Microsoft365:
                return !this._exposureControlService.enableMicrosoft365DataFlow;
            case DatasetFormatType.Oracle:
                return !this._exposureControlService.enableOracleDataFlow;
            case DatasetFormatType.Workday:
                return !GlobalVariables.isWorkdayEnabled;
            case DatasetFormatType.EventHubs:
                return !(GlobalVariables.enableEventHubsMDF || GlobalVariables.enableEventHubsCDC);
            case DatasetFormatType.Kafka:
                return !GlobalVariables.enableKafkaCDC;
            case DatasetFormatType.SapOData:
                return !this._exposureControlService.enableSAPOData;
            case DatasetFormatType.Cassandra:
                return !GlobalVariables.enableCassandraDataFlow;
            case DatasetFormatType.CosmosDBMongo:
                return !GlobalVariables.enableCosmosDBForMongoDB;
            case DatasetFormatType.Salesforce:
                return !GlobalVariables.enableSalesforceMDF;
            case DatasetFormatType.Iceberg:
                return !GlobalVariables.enableIcebergFormat;
            default:
                return false;
        }
    }

    private _inlineSinkHideByFeatureFlag(format: string): boolean {
        switch (format) {
            case DatasetFormatType.AdobeCampaign:
            case DatasetFormatType.AdobePrivacy:
                return !GlobalVariables.enableAdobeIntegration;
            case DatasetFormatType.Hive:
                return !this._exposureControlService.getCachedFeatureValue<boolean>(
                    GuardFeatures.ADFDataflowEnableHive
                );
            case DatasetFormatType.EventHubs:
                return !(GlobalVariables.enableEventHubsMDF || GlobalVariables.enableEventHubsCDC);
            case DatasetFormatType.Kafka:
                return !GlobalVariables.enableKafkaCDC;
            case DatasetFormatType.Cassandra:
                return !GlobalVariables.enableCassandraDataFlow;
            case DatasetFormatType.Iceberg: 
            return !GlobalVariables.enableIcebergFormat;
            default:
                return false;
        }
    }

    private _linkedServiceHideByFeatureFlag(linkedService: string, isSink: boolean): boolean {
        switch (linkedService) {
            case adfModelTypeMap.googleCloudStorage.linkedServiceType:
                return !this._exposureControlService.enableGoogleCloudStorageSourceDataFlow;
            case adfModelTypeMap.cassandra.linkedServiceType:
                return !GlobalVariables.enableCassandraDataFlow;
            default:
                return false;
        }
    }
}
